import {
  ConfigEnvironmentSettingsAccess,
  ConfigMetaData,
  ConfigurationSettingList,
  MergedConfigurationSettings,
  ValueWithSource,
} from "@kahunmedical/common-configuration";
import { ClinicConfig } from "fe-common/graphql/clinic/clinicTypes";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import { getV2ClinicSettings } from "./getV2ClinicSettings";
import {
  ConfigSettingsEnvironments,
  ConfigSettingsKeyPerEnvironment,
} from "./ConfigSettingsKeyPerNodeEnvironment";
import { ConfigurationSettingListWithSource } from "@kahunmedical/common-configuration/dist/lib/settings";
import { GetClinicConfigData } from "fe-common/graphql";
import { ClinicContextData } from "patient/src/clinic/clinicProvider";

export interface UseConfigurationSettingsManager {
  settings: MergedConfigurationSettings;
  setValue: (key, value) => void;
  getValue: (key: ConfigMetaData) => ValueWithSource;
  isDirty: boolean;
  setIsDirty: Dispatch<SetStateAction<boolean>>;
  getValueTree?: (rootKey: string) => ConfigurationSettingList;
  loading: boolean;
}

export const CLINIC_SOURCE_IDENTIFIER = "CLINIC";

export const useConfigurationSettingsManager = (
  initialSettings:
    | ConfigurationSettingListWithSource
    | [string, ConfigurationSettingList],
  clinicConfig?: Partial<
    Pick<GetClinicConfigData, "config" | "mergedConfiguration" | "name">
  >,
  currentEnvOverride = null,
): UseConfigurationSettingsManager => {
  const [loading, setLoading] = useState(true);

  const [settings, setSettings] = useState<MergedConfigurationSettings>(null);

  const [isDirty, setIsDirty] = useState(false);

  /// load initial default settings from environments, clinic, localstorage etc. only need to do once
  // (not sure where this code should go)
  useEffect(() => {
    setLoading(true);
    (async () => {
      const envStr =
        currentEnvOverride ||
        ConfigSettingsKeyPerEnvironment[process.env.REACT_APP_ENVIRONMENT] ||
        ConfigSettingsEnvironments.Development;

      // defaults for environment
      const envDefaults = await new ConfigEnvironmentSettingsAccess().getSettings(
        envStr,
      );

      let settingsManager = null;
      if (clinicConfig) {
        settingsManager = new MergedConfigurationSettings(
          {},
          initialSettings,
          clinicConfig.mergedConfiguration || [
            CLINIC_SOURCE_IDENTIFIER /*clinicConfig.clinicName,*/,
            getV2ClinicSettings(clinicConfig.config),
          ],
          envDefaults,
        );
      } else {
        settingsManager = new MergedConfigurationSettings(
          {},
          initialSettings,
          envDefaults,
        );
      }
      // merge all defaults
      setSettings(settingsManager);
      setLoading(false);
    })();
  }, [initialSettings, clinicConfig]);

  useEffect(() => console.log("settings changed!", settings), [settings]);

  const getValue = useCallback(
    (key: ConfigMetaData) => {
      const val = ConfigMetaData.isOption(key)
        ? settings?.getValueForMetaDataOption?.(key)
        : null;
      return val;
    },
    [settings],
  );

  const setValue = (key, value) => {
    console.log("useConfigurationSettingsManager", key, value);
    setSettings((oldSettings) => oldSettings?.withValue?.(key, value));
    setIsDirty(true);
  };

  return { settings, setValue, getValue, isDirty, setIsDirty, loading };
};
